import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  Loader,
  MiniTile,
  EmployeeTable,
  useReport,
  ExportCsvButton,
} from "../shared";

/**
 * CardFeeRecoverySection — dedicated ledger view for Dual Pricing
 * surcharges recovered over a date range.
 *
 * Guides the accountant from headline numbers → monthly trend →
 * per-tender split → the ticket-level table. Everything shares a single
 * ``/reports/card-fee-recovery`` fetch so the CSV export can't drift from
 * what's on screen.
 */
const MONTH_LABEL = (period) => {
  // 'YYYY-MM' → 'Feb 2026'
  const [y, m] = (period || "").split("-").map(Number);
  if (!y || !m) return period;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
};

const MonthlyTrend = ({ points }) => {
  if (!points || points.length === 0) {
    return (
      <div
        className="rounded-lg border border-dashed border-border p-8 text-sm text-muted-foreground text-center"
        data-testid="cfr-monthly-trend-empty"
      >
        No surcharges in this window yet — flip Dual Pricing on in POS Settings to start
        recovering card fees.
      </div>
    );
  }
  const data = points.map((p) => ({ ...p, label: MONTH_LABEL(p.period) }));
  return (
    <div
      className="rounded-lg border border-border p-3"
      data-testid="cfr-monthly-trend"
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          Monthly Recovery Trend
        </div>
        <div className="text-xs text-muted-foreground font-mono">
          {data[0].label} → {data[data.length - 1].label}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 11 }} width={50} />
          <Tooltip
            formatter={(v, key) =>
              key === "amount"
                ? [`$${Number(v).toFixed(2)}`, "Recovered"]
                : [v, "Orders"]
            }
            contentStyle={{ fontSize: 12 }}
          />
          <Bar dataKey="amount" fill="#059669" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const CardFeeRecoverySection = ({ filter }) => {
  const [data, loading] = useReport("/reports/card-fee-recovery", filter, false);
  if (loading) return <Loader />;
  if (!data) return null;
  const rangeLabel = `${data.start_date} → ${data.end_date}`;
  return (
    <div className="space-y-4" data-testid="cfr-section">
      <div className="flex justify-end">
        <ExportCsvButton
          path="/reports/card-fee-recovery/export.csv"
          filter={filter}
          isSingleDay={false}
          testId="export-cfr-csv"
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniTile
          label="Total Recovered"
          value={`$${data.total_recovered.toFixed(2)}`}
          tone="text-emerald-700"
          testId="cfr-total"
        />
        <MiniTile
          label="Orders w/ Surcharge"
          value={data.total_orders}
          testId="cfr-orders"
        />
        <MiniTile
          label="Avg per Order"
          value={`$${data.avg_surcharge.toFixed(2)}`}
          testId="cfr-avg"
        />
        <MiniTile
          label="Window"
          value={rangeLabel}
          valueClass="text-sm"
          testId="cfr-window"
        />
      </div>

      <MonthlyTrend points={data.by_month} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* By tender split */}
        <div
          className="rounded-lg border border-border overflow-hidden"
          data-testid="cfr-by-tender"
        >
          <div className="px-3 py-2 bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
            By Tender
          </div>
          <div className="divide-y divide-border max-h-64 overflow-y-auto">
            {data.by_tender.length === 0 && (
              <div className="px-3 py-3 text-sm text-muted-foreground">—</div>
            )}
            {data.by_tender.map((t) => (
              <div
                key={t.tender}
                className="px-3 py-2 flex items-center justify-between text-sm"
                data-testid={`cfr-tender-${t.tender}`}
              >
                <span className="capitalize">{t.tender.replace("_", " ")}</span>
                <span className="font-mono">
                  ${t.amount.toFixed(2)}{" "}
                  <span className="text-muted-foreground text-xs">({t.orders})</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily bucket recap */}
        <div
          className="rounded-lg border border-border overflow-hidden"
          data-testid="cfr-by-day"
        >
          <div className="px-3 py-2 bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
            Daily Recap ({data.by_day.length} day{data.by_day.length === 1 ? "" : "s"})
          </div>
          <div className="divide-y divide-border max-h-64 overflow-y-auto">
            {data.by_day.length === 0 && (
              <div className="px-3 py-3 text-sm text-muted-foreground">—</div>
            )}
            {data.by_day.map((d) => (
              <div
                key={d.period}
                className="px-3 py-2 flex items-center justify-between text-sm"
                data-testid={`cfr-day-${d.period}`}
              >
                <span className="font-mono text-muted-foreground">{d.period}</span>
                <span className="font-mono">
                  ${d.amount.toFixed(2)}{" "}
                  <span className="text-muted-foreground text-xs">({d.orders})</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ticket-level table */}
      <EmployeeTable
        rows={data.orders}
        columns={[
          {
            key: "created_at",
            label: "Time",
            fmt: (v) => (v || "").replace("T", " ").slice(0, 16),
          },
          { key: "short_id", label: "Order", fmt: (v) => `#${v}` },
          { key: "server_name", label: "Server", fmt: (v) => v },
          { key: "table_or_guest", label: "Table/Guest", fmt: (v) => v },
          { key: "tender", label: "Tender", fmt: (v) => v },
          {
            key: "total_charged",
            label: "Total",
            fmt: (v) => `$${v.toFixed(2)}`,
            align: "right",
          },
          {
            key: "surcharge",
            label: "Surcharge",
            fmt: (v) => `$${v.toFixed(2)}`,
            align: "right",
            bold: true,
            accent: true,
          },
        ]}
        testIdPrefix="cfr-row"
        emptyText="No surcharge-bearing orders in this window."
      />
    </div>
  );
};
